import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiOrb } from "../components/AiOrb";
import { useAuth } from "../context/AuthContext";
import { useSpeech } from "../hooks/useSpeech";
import { api } from "../lib/apiClient";
import { Power, Radio } from "lucide-react";

const STRIP = /<(do|search|read|render|watch|unwatch|agent|browse|build|mem|pref)>[\s\S]*?<\/\1>/gi;

/* Full-screen "TV / big-screen" mode: the animated Sara face + vocal interaction.
   Meant to be cast to a Smart TV / connected display and driven by voice. */
export default function TvMode() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    enableMic, micReady, recogSupported, startRecognition, stopRecognition,
    speak, stopSpeaking, speaking, unlockAudio, interim, micLevelRef, speakLevelRef,
  } = useSpeech();

  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");

  const state = speaking ? "speaking" : busy ? "thinking" : started && micReady && recogSupported ? "listening" : "idle";

  const handleSend = useCallback(async (text) => {
    if (!text || busy) return;
    unlockAudio();
    stopSpeaking();
    setBusy(true);
    setCaption("…");
    try {
      const { data } = await api.post("/chat", { message: text });
      const clean = (data.reply || "").replace(STRIP, "").trim();
      setBusy(false);
      setCaption(clean);
      speak(clean, user?.voice);
    } catch (e) {
      setBusy(false);
      const msg = "Erreur de liaison. Réessayez, je vous prie.";
      setCaption(msg);
      speak(msg, user?.voice);
    }
  }, [busy, speak, stopSpeaking, unlockAudio, user]);

  const handleSendRef = useRef(handleSend);
  useEffect(() => { handleSendRef.current = handleSend; }, [handleSend]);

  const onSpeechResult = useCallback(({ final }) => {
    if (final && final.trim()) handleSendRef.current(final.trim());
  }, []);

  // hands-free loop
  useEffect(() => {
    if (started && micReady && recogSupported && !speaking && !busy) {
      startRecognition(onSpeechResult);
    } else {
      stopRecognition();
    }
  }, [started, micReady, recogSupported, speaking, busy, startRecognition, stopRecognition, onSpeechResult]);

  const start = useCallback(async () => {
    unlockAudio();
    setStarted(true);
    if (recogSupported) enableMic().catch(() => {});
    const hello = `Bonjour ${user?.name || ""}. Je suis à l'écoute sur grand écran.`;
    setCaption(hello);
    speak(hello, user?.voice);
  }, [enableMic, recogSupported, speak, unlockAudio, user]);

  return (
    <div data-testid="tv-mode" className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-[#03060e]">
      {/* ambient backdrop */}
      <div className="pointer-events-none absolute inset-0 opacity-60"
        style={{ background: "radial-gradient(ellipse at 50% 35%, rgba(0,229,255,0.10), transparent 60%)" }} />

      {/* top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 py-4 sm:px-10">
        <div className="flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full"
            style={{ background: state === "listening" ? "#00ffaa" : state === "thinking" ? "#ffaa00" : "var(--accent)",
              boxShadow: "0 0 12px currentColor" }} />
          <div>
            <div className="font-display text-lg font-black tracking-[0.25em] accent-text text-glow">SARA CONNOR</div>
            <div className="font-mono-hud text-[9px] uppercase tracking-[0.35em] text-[#8b949e]">
              mode grand écran · {state === "listening" ? "écoute" : state === "thinking" ? "réflexion" : state === "speaking" ? "parole" : "veille"}
            </div>
          </div>
        </div>
        <button
          data-testid="tv-exit-button"
          onClick={() => { stopRecognition(); stopSpeaking(); navigate("/"); }}
          title="Quitter le mode TV"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border-hud)] text-[#8b949e] transition-colors hover:text-white"
        >
          <Power className="h-4 w-4" />
        </button>
      </div>

      {/* avatar stage */}
      <div className="relative z-10 flex flex-1 items-center justify-center">
        <AiOrb
          state={state}
          micLevelRef={micLevelRef}
          speakLevelRef={speakLevelRef}
          className="w-[min(92vw,720px)] aspect-square"
        />
      </div>

      {/* caption */}
      <div className="relative z-10 min-h-[96px] px-6 pb-10 sm:px-16 text-center">
        {interim && !busy && (
          <p className="mb-2 font-mono-hud text-sm text-[#00ffaa]/80">« {interim} »</p>
        )}
        {caption && (
          <p data-testid="tv-caption" className="mx-auto max-w-4xl text-xl leading-relaxed text-[#eaf4ff] drop-shadow sm:text-2xl">
            {caption}
          </p>
        )}
      </div>

      {/* start overlay */}
      {!started && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-[#03060e]/80 backdrop-blur-sm">
          <div className="text-center">
            <div className="font-display text-3xl font-black tracking-[0.3em] accent-text text-glow sm:text-5xl">SARA CONNOR</div>
            <p className="mt-3 font-mono-hud text-xs uppercase tracking-[0.3em] text-[#8b949e]">mode grand écran</p>
          </div>
          <button
            data-testid="tv-start-button"
            onClick={start}
            className="hud-btn clip-corner flex items-center gap-3 px-8 py-4 text-base font-bold"
          >
            <Radio className="h-5 w-5" /> ACTIVER SARA
          </button>
          <p className="max-w-md px-6 text-center font-mono-hud text-[11px] leading-relaxed text-[#556]">
            {recogSupported
              ? "Cliquez pour activer le son et le micro, puis parlez librement."
              : "Ce grand écran ne dispose pas de micro — pilotez Sara depuis votre téléphone."}
          </p>
        </div>
      )}
    </div>
  );
}
